// @ts-ignore no types
import React, { useEffect, useMemo, useRef } from 'react'
// @ts-ignore no types
import _ from 'lodash'
import { enums } from '@adc/sdk'
import type { Listing } from '@adc/sdk/types'
import * as blueprint from '@adc/blueprint'
import type {
  ListingPageActionsType,
  ListingPageType,
} from '@adc/blueprint/lib/listing/pdp/hooks'
// @ts-ignore no types
import Helmet from 'react-helmet'
import * as graph from '@adc/sdk'
// @ts-ignore no types
import { connect, useDispatch } from 'react-redux'
// @ts-ignore no types
import { standardSchemas } from '@adc/analytics'
import config from '@adc/configment'
import {
  AuctionApplication,
  ChatButton,
  NullComponent,
  useApplicationContext,
  withApplicationContext,
  ApplicationNotificationsConsumer,
  // @ts-ignore cannot find module but module exists
} from '@adc/ui-adc'
import { MarketingAnalysisProvider } from 'common/hooks/useSelectMarketAnalysisCard'
// @ts-ignore cannot find module but module exists
import { getMetaContent } from 'common/utils/headers'
import { toggleSaveProperty } from 'common/utils/property'
import auctionSmartAppImg from '../images/auction_smart_banner_logo.svg'
import { onAuthenticationComplete } from '../services/user'
import { setApplicationNotificationsContext } from '../utils/applicationNotificationsBridge'
import { appVariants } from '../reducers/app'
import Property from './property'
import CancelledProperty from './cancelledProperty'
import SparkOfferProperty from './sparkOfferProperty'
import VerificationModal from './verificationModal'
import SmsOptInModal from './smsOptInModal'
import RedesignedProperty from './propertyRedesign'

const PDPVariant = {
  [appVariants.DEFAULT]: Property,
  [appVariants.CANCELLED]: CancelledProperty,
  [appVariants.SPARK_OFFER]: SparkOfferProperty,
  [appVariants.REDESIGN]: RedesignedProperty,
}

interface AuctionAppProps {
  user: blueprint.primitives.AuctionUser
  initialListing: Listing
  featureFlags: any
  onAuthenticationComplete: any
  variant: string
  experiments: any
  isSaved: boolean
}

const AuctionApp = ({
  user,
  initialListing,
  featureFlags,
  onAuthenticationComplete,
  variant,
  experiments,
}: AuctionAppProps) => {
  const { embeddedContext } = useApplicationContext()
  const initialValues = useMemo(
    () => ({
      initialData: [initialListing],
      featureFlags,
      isLoggedIn: user.logged_in,
    }),
    [initialListing, featureFlags]
  )

  const initialSavedPropertiesValues = useMemo(
    () => ({
      initialData: initialListing.primary_property.is_currently_saved
        ? [initialListing.primary_property]
        : [],
      isLoggedIn: user.logged_in,
    }),
    [initialListing, featureFlags]
  )
  const { data, actions } = blueprint.listing.hooks.usePage(initialValues)
  const { data: savedPropertiesData, actions: savedPropertiesActions } =
    blueprint.user.hooks.useSavedProperties(initialSavedPropertiesValues)
  // TODO I do not like this,I would rather prefer to just use the savedPropertiesActions and pass the expected boolean, but we are still coupled to redux updates. When we get away from it we can find an alternative.
  const dispatch = useDispatch()
  const propertyActions = {
    toggleSaveProperty: toggleSaveProperty(
      savedPropertiesActions.updateSaveProperty,
      dispatch,
      user
    ),
  }
  const listing = featureFlags['app.enable-pdp-design-2025']
    ? data.currentListing
    : {
        ...initialListing,
        // the reason we are adding __computed__ is because the due
        // diligence section and additional document section rely
        // on __computed__ values to display content, regardless
        // of the status of the pdp-design flag
        __computed__: data.currentListing.__computed__,
      }

  const property = {
    ...listing.primary_property,
    ...savedPropertiesData.savedProperties.mapped.get(
      listing.primary_property.property_id
    ),
  }

  // used to hand off element where we want to render page banner
  const isPrivateSeller = blueprint.listing.utils.isPrivateSeller(listing)
  const pageBannerRef = useRef(null)
  const addressString = listing.formatted_address
    ? listing.formatted_address.join(', ')
    : ''
  const websiteName = isPrivateSeller ? 'SmartSale' : 'Auction.com'
  const headerTitle = addressString
    ? `${addressString} | ${websiteName}`
    : websiteName

  const metaContent = getMetaContent(listing)
  const headerProps = {
    featureFlags,
    showLiveChat: true,
    showSearch: true,
  }
  const footerProps = {
    useDeprecatedPageWidth: true,
  }

  const ADC_LOGO = config.get('AUCTIONCOM_LOGO_URL', '')
  const headerDataStructureSchemas = [
    standardSchemas.corporateSchema(ADC_LOGO),
    standardSchemas.searchSiteSchema(),
    standardSchemas.breadCrumbSchema(listing),
    standardSchemas.eventSchema(
      listing,
      config.get('LISTING_DEFAULT_PHOTO_URL', '')
    ),
    standardSchemas.productSchema(
      listing,
      config.get('LISTING_DEFAULT_PHOTO_URL', '')
    ),
    standardSchemas.organizationSchema(ADC_LOGO),
    standardSchemas.webPageSchema(
      'https://www.auction.com/details',
      headerTitle,
      metaContent,
      standardSchemas.breadCrumbSchema(listing),
      [
        `${listing?.primary_property?.summary?.address.country_secondary_subdivision}`,
        `${listing?.primary_property?.summary?.address.country_secondary_subdivision} County`,
        `${listing?.primary_property.summary?.address.country_primary_subdivision}`,
        `${listing?.primary_property.summary?.address.street_name}`,
        `${listing?.primary_property.summary?.address.municipality}`,
        `${listing?.primary_property.summary?.street_address}`,
      ]
    ),
    ...(listing.primary_property.summary?.structure_type_group ===
    enums.PropertyStructureTypeGroup.SingleFamily
      ? [standardSchemas.singleFamilyHomeSchema(listing)]
      : []),
  ]

  const helmetConfig = {
    title: `${headerTitle}`,
    description: metaContent,
    children: (
      <script type='application/ld+json'>
        {JSON.stringify(headerDataStructureSchemas)}
      </script>
    ),
  }

  const disablePdpMobileBanner =
    listing.listing_configuration?.asset_type ===
    graph.enums.AssetType.BankOwned

  const capabilities = useMemo(() => {
    return {
      notifications: {
        pageBannerRef,
      },
      confirmation: {
        enabled: true,
      },
      authentication: {
        enabled: true,
        options: {
          onAuthenticationComplete: (props: {
            user: blueprint.primitives.AuctionUser
          }) =>
            onAuthenticationComplete({
              ...props,
              refreshPageData: actions.refresh,
              listing,
            }),
        },
      },
    }
  }, [onAuthenticationComplete, pageBannerRef])

  const SelectedPDP: (props: any) => React.JSX.Element = PDPVariant[variant]

  /**
   * We allow the default footer to come through as it knows how to behave when it's in webview mode...
   */
  const overridableComponents =
    embeddedContext === 'webview'
      ? {
          header: <NullComponent />,
        }
      : {}

  return (
    <AuctionApplication
      user={user}
      sspConfig={
        disablePdpMobileBanner
          ? _.omit(config.get(), [
              'appstore.adc.GOOGLE_PLAY_ID',
              'appstore.adc.APPLE_ITUNES_ID',
            ])
          : config.get()
      }
      headerProps={headerProps}
      footerProps={footerProps}
      helmetConfig={helmetConfig}
      featureFlags={featureFlags}
      capabilities={capabilities}
      experiments={experiments}
      overridableComponents={overridableComponents}
    >
      <Helmet
        link={[
          { rel: 'android-touch-icon', href: auctionSmartAppImg },
          { rel: 'apple-touch-icon', href: auctionSmartAppImg },
        ]}
      />
      <ApplicationNotificationsConsumer>
        {
          // @ts-ignore no types
          context => {
            setApplicationNotificationsContext(context)
            return null
          }
        }
      </ApplicationNotificationsConsumer>
      <main>
        <MarketingAnalysisProvider>
          <SelectedPDP
            pageBannerRef={pageBannerRef}
            initialListing={listing}
            activeListings={data.activeListings.raw}
            listingActions={actions}
            property={property}
            propertyActions={propertyActions}
          />
          {featureFlags['fe.resi.live-chat-redesign'] && (
            <ChatButton isFixedPosition />
          )}
          {user.logged_in && <VerificationModal />}
          <SmsOptInModal />
        </MarketingAnalysisProvider>
      </main>
    </AuctionApplication>
  )
}

const mapStateToProps = (store: any) => ({
  initialListing: store.listing,
  user: store.user,
  featureFlags: store.featureFlags,
  variant: store.app.variant,
})

const mapDispatchToProps = (dispatch: any) => ({
  // AuthModal required capabilites
  onAuthenticationComplete: (props: {
    user: blueprint.primitives.AuctionUser
    refreshPageData: ListingPageActionsType['refresh']
    listing: ListingPageType['currentListing']
  }) => dispatch(onAuthenticationComplete(props)),
})

export default withApplicationContext(
  connect(mapStateToProps, mapDispatchToProps)(AuctionApp)
)
